import React from 'react'
import clsx from 'clsx'
import {
  AppAccordion,
  AppAccordionItem,
  AppAccordionTrigger,
  AppAccordionContent,
} from '@easyllama/ui-components'

const FAQ_ITEMS = [
  {
    id: 'q1',
    question: "How does EasyLlama's pricing work?",
    answer: 'The Pay Per Course plan lets you purchase individual course assignments (10 course minimum) at $24.95 each. Each course assignment is used when assigned to one learner. Core and Ultimate are priced per user per year and include unlimited access to the full course library. You only pay for the users or course assignments you actually need.',
  },
  {
    id: 'q2',
    question: "What's included in every plan?",
    answer: 'All three plans include access to EasyLlama’s library of 450+ legally-vetted courses, EasyLlama AI Compliance Automations, the Anonymous Reporting Tool, Document & Policy Manager, 50+ HRIS and payroll integrations, and LMS capabilities like Course Customizations, Classroom/Kiosk Mode, Learning Journeys, Workflow Automations, and an AI-powered Reporting Assistant.',
  },
  {
    id: 'q3',
    question: "What's the difference between the Core, and Ultimate plans?",
    answer: 'Core includes our full compliance training library plus the AI-Powered Phishing Simulator with phishing templates, real-time threat alerts, and interactive micro-lessons for every employee. Ultimate includes everything in Core, plus AI Course Authoring tools that let teams create, edit, and publish custom training courses using AI-generated content, visuals, narration, and interactive templates.',
  },
  {
    id: 'q4',
    question: 'Can I switch plans later?',
    answer: "Yes. You can upgrade or downgrade your plan at any time. When you upgrade, new features become available immediately and we’ll prorate the difference. Downgrades take effect at the end of your current billing cycle. If you’re currently on a legacy plan, changing plans or adding new features may move your account to the new pricing and packaging structure.",
  },
  {
    id: 'q5',
    question: 'Is there a free trial available?',
    answer: 'While we don’t currently offer a free trial, our team provides personalized demos so you can see how EasyLlama would work for your specific organization and training requirements. Click “Get a Demo” on any plan to schedule a session.',
  },
  {
    id: 'q6',
    question: 'What size companies is EasyLlama best for?',
    answer: 'EasyLlama is a strong fit for companies of all sizes that want a simpler, more scalable way to manage compliance training. The platform is especially valuable for teams managing training across multiple locations, roles, and requirements, without relying on manual tracking or disconnected tools.',
  },
  {
    id: 'q7',
    question: 'Do you integrate with our existing HRIS and payroll systems',
    answer: 'Yes. EasyLlama integrates with 50+ HRIS and payroll platforms including Workday, BambooHR, Rippling, ADP, Gusto, Paylocity, and many more. Integrations sync employees automatically so onboarding and offboarding stay in sync with your HRIS.',
  },
  {
    id: 'q8',
    question: 'Does EasyLlama have state-specific compliance courses?',
    answer: 'Yes. EasyLlama integrates with 50+ HRIS and payroll platforms including Workday, BambooHR, Rippling, ADP, Gusto, Paylocity, and many more. Employee data syncs automatically so onboarding, offboarding, and training assignments stay current without manual updates.',
  },
  {
    id: 'q9',
    question: 'How does the SCORM plan work?',
    answer: (
      <>
        The SCORM plan lets you use EasyLlama courses inside your existing LMS. We package courses as SCORM 1.2, SCORM 2004, or xAPI files that can be uploaded directly into your system, with support for compatibility testing, custom completion tracking, and bulk course packaging.
        <br /><br />
        Contact sales for pricing and compatibility testing with your LMS.
      </>
    )
  },
  {
    id: 'q10',
    question: 'What does the Customization Services plan include?',
    answer: (
      <>
        Customization Services includes full branding and white-labeling, custom course development by our in-house content experts tailored to your company policies and training needs, custom integrations and API access, SSO/SAML authentication, a dedicated account manager, and priority support with a custom SLA.
        <br /><br />
        Pricing is tailored to each organization based on scope and desired support. Contact sales for pricing.
      </>
    ),
  },
  {
    id: 'q11',
    question: 'Is my data secure?',
    answer: 'Yes. EasyLlama is SOC 2 Type II certified, GDPR and CCPA compliant, and uses enterprise-grade encryption to protect your data both in transit and at rest. From anonymous reporting to document management, the platform is designed to help organizations manage sensitive employee and compliance data securely.',
  },
]

export default function BillingFAQ({ fullBleed = false, contentClassName = 'w-full', items = FAQ_ITEMS, contentPaddingClassName = 'px-8' }) {
  const bleedClassName = fullBleed
    ? 'w-screen relative left-1/2 -translate-x-1/2'
    : '-mx-6'

  return (
    // data-safari-* markers restore this spacing inside PlansDialog; see
    // _in_app_announcement_styling.html.erb. Each names the utility it restores.
    <div
      className={clsx('py-10 px-6 bg-llama-grey-9 lining-nums', bleedClassName)}
      data-safari-py-10
      data-safari-px-6
      {...(fullBleed ? {} : { 'data-safari-mx-n6': true })}
    >
      <h3 className='mb-8 text-center font-[RecoletaSemiBold] text-2xl font-semibold text-llama-grey-7' data-safari-mb-8 data-safari-text-center>
        Frequently Asked Questions
      </h3>

      {/* Marker only when the padding really is px-8 — a caller passing other padding must not have
          px-8 forced back on it. */}
      <div
        className={clsx('mx-auto', contentPaddingClassName, contentClassName)}
        data-safari-mx-auto
        {...(contentPaddingClassName === 'px-8' ? { 'data-safari-px-8': true } : {})}
      >
        <AppAccordion collapsible type='single' variant='lg'>
          {items.map((item) => (
            <AppAccordionItem key={item.id} value={item.id} className='mb-3'>
              <AppAccordionTrigger className='font-semibold text-sm text-llama-grey-7'>
                {item.question}
              </AppAccordionTrigger>
              <AppAccordionContent className='text-sm leading-relaxed text-llama-grey-7'>
                {item.answer}
              </AppAccordionContent>
            </AppAccordionItem>
          ))}
        </AppAccordion>
      </div>
    </div>
  )
}
